import type {
  HTMLAttributes,
  Ref,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from "react";
import { cn } from "@/lib/utils";

export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[var(--color-border-subtle)] bg-[rgba(255,255,255,0.02)]">
      <table className={cn("w-full text-[13px]", className)} {...props} />
    </div>
  );
}

export function TableHead({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn(className)} {...props} />;
}

export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn(className)} {...props} />;
}

/**
 * `ref` is declared explicitly: React 19 passes it to function components as a
 * plain prop, but the type has to say so for the row-navigation hook to attach
 * to it.
 */
export function TableRow({
  className,
  ref,
  ...props
}: HTMLAttributes<HTMLTableRowElement> & { ref?: Ref<HTMLTableRowElement> }) {
  return (
    <tr
      ref={ref}
      className={cn(
        "border-b border-[var(--color-border-subtle)] last:border-b-0 hover:bg-[rgba(255,255,255,0.025)]",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:-ring-offset-1 focus-visible:ring-[var(--color-accent)]",
        className,
      )}
      {...props}
    />
  );
}

export function TableHeaderCell({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        "sticky top-0 z-10 border-b border-[var(--color-border-subtle)] bg-black/30 px-3 py-2 text-left",
        "text-[11px] font-medium uppercase tracking-wider text-[var(--color-fg-subtle)] backdrop-blur",
        className,
      )}
      {...props}
    />
  );
}

export function TableCell({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      className={cn("px-3 py-2 align-middle text-[var(--color-fg-muted)]", className)}
      {...props}
    />
  );
}
